/**
 * Device Mode Detection
 *
 * A state machine for detecting whether the user is using a mouse or trackpad.
 * Instead of analyzing each event individually, maintains an assumed mode and
 * requires stronger evidence to switch modes (hysteresis).
 *
 * This provides immediate response within a mode without hedging/buffering,
 * while preventing flip-flopping between modes.
 */

import type { Platform, Browser } from './types';

// ============================================================================
// TYPES
// ============================================================================

/** The assumed input device mode */
export type InputDeviceMode = 'mouse' | 'trackpad';

/** Thresholds for mode switching (higher than in-mode detection) */
export interface ModeSwitchThresholds {
	/** Delta magnitude that strongly indicates mouse wheel (switch to mouse mode) */
	mouseWheelThreshold: number;
	/** Horizontal movement that strongly indicates trackpad (switch to trackpad mode) */
	horizontalThreshold: number;
	/** High frequency + small deltas indicates trackpad */
	trackpadFrequencyThreshold: number; // events per second
	trackpadSmallDeltaThreshold: number;
	/** Idle time before mode can be reconsidered (ms) */
	idleTimeoutMs: number;
}

/** Signals available for guessing initial mode */
export interface InitialModeSignals {
	platform: Platform;
	browser: Browser;
	/** Screen pixel density (devicePixelRatio) */
	pixelRatio?: number;
	/** Whether device has touch capability */
	hasTouch?: boolean;
	/** Screen width in pixels */
	screenWidth?: number;
}

export interface WheelEventInfo {
	deltaX: number;
	deltaY: number;
	ctrlKey: boolean;
	timestamp: number;
}

export interface DeviceModeState {
	mode: InputDeviceMode;
	/** Confidence in current mode (0-1) */
	confidence: number;
	/** Time of last event */
	lastEventTime: number;
	/** Recent event count for frequency calculation */
	recentEventCount: number;
	/** Time window start for frequency calculation */
	frequencyWindowStart: number;
}

// ============================================================================
// DEFAULT THRESHOLDS
// ============================================================================

const DEFAULT_THRESHOLDS: ModeSwitchThresholds = {
	// Conservative thresholds - bias toward mouse/zoom mode
	// Rationale: desktop mouse users are more common, and zoom-instead-of-pan
	// is less disruptive than pan-instead-of-zoom for mouse users
	mouseWheelThreshold: 50, // Large discrete delta = definitely mouse (lowered to catch more mice)
	horizontalThreshold: 20, // Need significant horizontal to indicate trackpad (raised for conservatism)
	trackpadFrequencyThreshold: 40, // 40+ events/sec = definitely smooth trackpad scrolling (raised)
	trackpadSmallDeltaThreshold: 10, // Small deltas at high frequency (lowered for strictness)
	idleTimeoutMs: 500, // After idle, more willing to reconsider mode
};

// ============================================================================
// INITIAL MODE GUESSING
// ============================================================================

/**
 * Guess the initial device mode based on platform signals.
 * This is an encapsulated function that can be enhanced with more signals later.
 *
 * Current heuristics:
 * - Mac laptops commonly have trackpads, but default to mouse (user expectation for zoom)
 * - Windows/Linux default to mouse (more common)
 * - High DPI screens on Mac might indicate MacBook (trackpad likely)
 * - Touch capability might indicate convertible/tablet (trackpad-like)
 *
 * For now, we default to 'mouse' since:
 * 1. Mouse users expect wheel = zoom (Miro-like behavior)
 * 2. It's easier to detect trackpad from events than vice versa
 * 3. False positive (trackpad user in mouse mode) = zoom instead of pan, less jarring
 */
export function guessInitialMode(signals: InitialModeSignals): InputDeviceMode {
	// Future enhancement points:
	// - signals.pixelRatio > 1 on Mac might indicate Retina MacBook (trackpad)
	// - signals.hasTouch on non-tablet might indicate convertible
	// - signals.screenWidth < 1500 on Mac might indicate MacBook (vs external display)

	// For now, always default to mouse mode
	// This matches user expectation: wheel = zoom (Miro-like)
	// Trackpad users will naturally trigger mode switch via horizontal movement or ctrlKey pinch
	return 'mouse';
}

// ============================================================================
// DEVICE MODE DETECTOR
// ============================================================================

export interface DeviceModeDetector {
	/** Get current assumed mode */
	getMode(): InputDeviceMode;

	/** Get full state for debugging */
	getState(): DeviceModeState;

	/**
	 * Process a wheel event and determine the action.
	 * Returns what action to take based on current mode.
	 *
	 * Note: ctrlKey events are ALWAYS zoom (trackpad pinch) regardless of mode.
	 */
	processEvent(event: WheelEventInfo): {
		action: 'zoom' | 'pan';
		source: 'mouse' | 'trackpad';
		/** Whether this event caused a mode switch */
		modeSwitched: boolean;
	};

	/**
	 * Signal that a touch event occurred.
	 * This is a strong indicator that the device has a trackpad.
	 * Call this when touchstart/touchmove events are received.
	 */
	onTouchEvent(): void;

	/** Force a specific mode (for testing or user preference) */
	setMode(mode: InputDeviceMode): void;

	/** Reset to initial state */
	reset(): void;

	/** Update thresholds */
	setThresholds(thresholds: Partial<ModeSwitchThresholds>): void;

	/** Get current thresholds */
	getThresholds(): ModeSwitchThresholds;
}

export interface DeviceModeDetectorOptions {
	initialMode?: InputDeviceMode;
	thresholds?: Partial<ModeSwitchThresholds>;
	signals?: InitialModeSignals;
}

export function createDeviceModeDetector(
	platform: Platform,
	browser: Browser,
	options?: DeviceModeDetectorOptions
): DeviceModeDetector {
	const signals: InitialModeSignals = {
		platform,
		browser,
		...options?.signals,
	};

	let thresholds: ModeSwitchThresholds = {
		...DEFAULT_THRESHOLDS,
		...options?.thresholds,
	};

	const initialMode = options?.initialMode ?? guessInitialMode(signals);

	let state: DeviceModeState = {
		mode: initialMode,
		confidence: 0.5, // Start with medium confidence
		lastEventTime: 0,
		recentEventCount: 0,
		frequencyWindowStart: 0,
	};

	const FREQUENCY_WINDOW_MS = 200; // Window for calculating event frequency

	function updateFrequencyTracking(timestamp: number): number {
		// Reset window if too old
		if (timestamp - state.frequencyWindowStart > FREQUENCY_WINDOW_MS) {
			state.frequencyWindowStart = timestamp;
			state.recentEventCount = 1;
			return 0;
		}

		state.recentEventCount++;

		// Calculate frequency (events per second)
		const elapsed = timestamp - state.frequencyWindowStart;
		if (elapsed > 0) {
			return (state.recentEventCount / elapsed) * 1000;
		}
		return 0;
	}

	function shouldSwitchToTrackpad(
		event: WheelEventInfo,
		frequency: number,
		isIdle: boolean
	): boolean {
		const { deltaX, deltaY } = event;
		const deltaMagnitude = Math.abs(deltaY);
		const hasSignificantHorizontal = Math.abs(deltaX) >= thresholds.horizontalThreshold;
		const hasHighFrequency = frequency >= thresholds.trackpadFrequencyThreshold;
		const hasSmallDeltas = deltaMagnitude <= thresholds.trackpadSmallDeltaThreshold;

		// Require multiple signals to switch to trackpad mode (conservative)
		// Option 1: Significant horizontal movement WITH small vertical deltas
		if (hasSignificantHorizontal && hasSmallDeltas) {
			return true;
		}

		// Option 2: High frequency WITH small deltas (smooth scrolling pattern)
		if (hasHighFrequency && hasSmallDeltas) {
			return true;
		}

		return false;
	}

	function shouldSwitchToMouse(event: WheelEventInfo, isIdle: boolean): boolean {
		const { deltaY, deltaX } = event;

		// Strong indicator: large discrete delta with no horizontal
		const deltaMagnitude = Math.abs(deltaY);
		const hasHorizontal = Math.abs(deltaX) > 2;

		if (deltaMagnitude >= thresholds.mouseWheelThreshold && !hasHorizontal) {
			return true;
		}

		// After idle, be more willing to switch if we see mouse-like behavior
		// (medium delta, no horizontal) - threshold is halved
		if (isIdle && deltaMagnitude >= thresholds.mouseWheelThreshold / 2 && !hasHorizontal) {
			return true;
		}

		return false;
	}

	return {
		getMode(): InputDeviceMode {
			return state.mode;
		},

		getState(): DeviceModeState {
			return { ...state };
		},

		processEvent(event: WheelEventInfo): {
			action: 'zoom' | 'pan';
			source: 'mouse' | 'trackpad';
			modeSwitched: boolean;
		} {
			const now = event.timestamp;
			const isIdle = now - state.lastEventTime > thresholds.idleTimeoutMs;

			// Update tracking
			const frequency = updateFrequencyTracking(now);
			state.lastEventTime = now;

			// ctrlKey ALWAYS means trackpad pinch → zoom
			// This is browser-synthesized for trackpad pinch gestures
			if (event.ctrlKey) {
				// Don't switch mode for ctrlKey events - they're special
				return {
					action: 'zoom',
					source: 'trackpad',
					modeSwitched: false,
				};
			}

			// Capture mode at start of processing - this determines the action
			// for THIS event, even if the mode switches for future events
			const modeAtStart = state.mode;
			let modeSwitched = false;

			// Check for mode switching
			if (state.mode === 'mouse') {
				if (shouldSwitchToTrackpad(event, frequency, isIdle)) {
					state.mode = 'trackpad';
					state.confidence = 0.7;
					modeSwitched = true;
				}
			} else {
				// trackpad mode
				if (shouldSwitchToMouse(event, isIdle)) {
					state.mode = 'mouse';
					state.confidence = 0.7;
					modeSwitched = true;
				}
			}

			// Return action based on mode AT START of processing
			// This ensures trackpad mode always returns pan for the current event,
			// even if this event triggered a switch to mouse mode for future events
			if (modeAtStart === 'mouse') {
				return {
					action: 'zoom',
					source: 'mouse',
					modeSwitched,
				};
			} else {
				return {
					action: 'pan',
					source: 'trackpad',
					modeSwitched,
				};
			}
		},

		setMode(mode: InputDeviceMode): void {
			state.mode = mode;
			state.confidence = 1.0; // User explicitly set, high confidence
		},

		onTouchEvent(): void {
			// Touch event is a strong signal that device has a trackpad
			// Switch to trackpad mode with high confidence
			if (state.mode !== 'trackpad') {
				state.mode = 'trackpad';
				state.confidence = 0.9;
			}
		},

		reset(): void {
			state = {
				mode: guessInitialMode(signals),
				confidence: 0.5,
				lastEventTime: 0,
				recentEventCount: 0,
				frequencyWindowStart: 0,
			};
		},

		setThresholds(newThresholds: Partial<ModeSwitchThresholds>): void {
			thresholds = { ...thresholds, ...newThresholds };
		},

		getThresholds(): ModeSwitchThresholds {
			return { ...thresholds };
		},
	};
}
